import { Grid, Paper, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Bar, Doughnut } from "react-chartjs-2";
import React from "react";
import { useParams } from "react-router-dom";
import { GET_INSTAGRAM_AUDIENCE } from "../../services/instagram_analytics";
import {
    AnalyticsEmptyState,
    CardSection,
    InstagramAnalyticsHeader,
    formatDate,
    parseAudienceEntries,
    useInstagramAccounts,
    useInstagramSync,
} from "./shared";

export default function InstagramAnalyticsAudiencePage() {
    const { oauthId } = useParams();
    const { accounts, loadingAccounts } = useInstagramAccounts();

    const [loading, setLoading] = React.useState<boolean>(true);
    const [audience, setAudience] = React.useState<any>(null);

    const loadData = React.useCallback(async () => {
        if (!oauthId) return;

        try {
            setLoading(true);
            const response = await GET_INSTAGRAM_AUDIENCE(oauthId);
            setAudience(response?.audience || null);
        } catch (err: any) {
            showNotification({ message: err.message, color: "red" });
        } finally {
            setLoading(false);
        }
    }, [oauthId]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

    const genderRows = parseAudienceEntries(audience?.gender_distribution);
    const ageRows = parseAudienceEntries(audience?.age_ranges);
    const cityRows = parseAudienceEntries(audience?.top_cities);
    const countryRows = parseAudienceEntries(audience?.top_countries);

    const isEmpty = !loading && !audience;

    return (
        <Stack>
            <InstagramAnalyticsHeader
                oauthId={oauthId}
                title="Resumo de audiência"
                subtitle="Visão demográfica"
                active="audience"
                accounts={accounts}
                loadingAccounts={loadingAccounts}
                syncing={syncing}
                onSync={triggerSync}
            />

            <Paper withBorder p="md">
                <Text size="sm" c="dimmed">
                    Última atualização: {formatDate(audience?.date)}
                </Text>
            </Paper>

            {isEmpty ? (
                <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
            ) : (
                <Grid>
                    <Grid.Col md={4}>
                        <CardSection title="Distribuição por gênero">
                            <Doughnut
                                data={{
                                    labels: genderRows.map((item) => item.label),
                                    datasets: [
                                        {
                                            data: genderRows.map((item) => item.value * 100),
                                            backgroundColor: ["#f43f5e", "#2563eb", "#f59e0b", "#14b8a6"],
                                        },
                                    ],
                                }}
                            />
                        </CardSection>
                    </Grid.Col>

                    <Grid.Col md={8}>
                        <CardSection title="Faixas etárias">
                            <Bar
                                data={{
                                    labels: ageRows.map((item) => item.label),
                                    datasets: [
                                        {
                                            label: "% da audiência",
                                            data: ageRows.map((item) => item.value * 100),
                                            backgroundColor: "#7c3aed99",
                                        },
                                    ],
                                }}
                                options={{
                                    plugins: { legend: { position: "top" as const } },
                                }}
                            />
                        </CardSection>
                    </Grid.Col>

                    <Grid.Col md={6}>
                        <Paper withBorder p="md" h="100%">
                            <Text fw={600} mb="sm">Principais cidades</Text>
                            <Stack spacing={6}>
                                {cityRows.map((item) => (
                                    <Text key={item.label} size="sm">
                                        {item.label}: {(item.value * 100).toFixed(2)}%
                                    </Text>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid.Col>

                    <Grid.Col md={6}>
                        <Paper withBorder p="md" h="100%">
                            <Text fw={600} mb="sm">Principais países</Text>
                            <Stack spacing={6}>
                                {countryRows.map((item) => (
                                    <Text key={item.label} size="sm">
                                        {item.label}: {(item.value * 100).toFixed(2)}%
                                    </Text>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid.Col>
                </Grid>
            )}
        </Stack>
    );
}
